// Chapter text shared by the React components and the Worker's crawler HTML.
// Icons stay in the components; entries are matched to them by title.
import { ARTIST } from "../lib/site";

export const HERO = {
  statement: "Mithila, reimagined as a living language of line, colour and devotion.",
  welcome:
    "Welcome to my digital gallery — original Madhubani paintings, the stories they carry, and the living world of Mithila behind them.",
};

export const ARTIST_LEAD =
  "I returned to my first language — drawing — and found in the lines of Mithila a practice that is at once artistic, cultural and spiritual.";

export const ARTIST_QUOTE =
  "Mithila painting has become not just an artistic practice for me, but a meaningful cultural and spiritual journey.";

export const STAGES = [
  {
    mark: "Childhood",
    title: "A Childhood Filled With Drawings",
    text: "I always enjoyed drawing. The last pages of my school notebooks were filled with peacocks, fish, lotuses and ducks — drawn again and again, long before I knew the name Mithila.",
  },
  {
    mark: "The middle years",
    title: "A Life That Took Another Path",
    text: "Education, marriage, work, raising children and supporting my husband's career abroad left little time for art. The notebooks closed — but the inclination never left me.",
  },
  {
    mark: "2012 – 2015 · Patna",
    title: "A Connection With Mithila",
    text: "From 2012 to 2015 I lived in Pataliputra Colony, Patna, and often visited local exhibitions where Mithila paintings fascinated me. When I moved to Bhopal, I carried the tradition with me — paintings, textiles, sarees and cushion covers that kept the connection alive.",
  },
  {
    mark: "2020 · The turning point",
    title: "Rediscovering Art During COVID",
    text: "When life suddenly slowed, my long-standing passion returned. I began drawing every day — one small Mithila painting at a time — as if reconnecting with a part of myself that had waited patiently for years.",
  },
  {
    mark: "Practice",
    title: "From Practice to Expression",
    text: "Encouraged by a watercolour artist, I moved from small sheets to larger formats, and from recreating traditional compositions to bringing my own ideas and interpretations into the work.",
  },
  {
    mark: "Study",
    title: "Learning the Tradition",
    text: "Seeking authentic learning, I joined Shilp Kala Vidyapeeth, where I study the finer details of Mithila painting through traditional methods, along with the mythology, culture and festivals of the Mithila region.",
  },
];

export const WORLD_INTRO = [
  "In the Mithila region of Bihar, painting was never separate from life. Walls, floors and courtyards bloomed with line and colour at weddings, births and festivals — a tradition carried largely by women for centuries, and one I now practise and study on paper and canvas.",
  "Every painting speaks in a grammar of motifs. Empty space is filled with intention — fine hatching, double-line borders and geometric repeats — so that the picture plane hums, edge to edge, with quiet energy.",
];

export const MOTIFS = [
  {
    title: "Fish",
    text: "A sign of fertility, abundance and good fortune — fish move through Mithila paintings the way water moves through the land of Mithila itself.",
  },
  {
    title: "Lotus",
    text: "Purity rising from still water. The lotus anchors prayers, goddesses and thresholds; its petals also give Mithila borders their rhythm.",
  },
  {
    title: "Peacock",
    text: "Love, longing and monsoon joy. The peacock's arched neck and fanned tail are among the most beloved figures in the Madhubani repertoire.",
  },
  {
    title: "Sun & Moon",
    text: "Painted as watchful faces, the sun and moon mark cosmic order — invoked at weddings and festivals as witnesses and protectors.",
  },
  {
    title: "Tree of Life",
    text: "Bamboo and flowering trees stand for lineage and continuity, often filling the kohbar — the marriage chamber painting — with wishes for a growing family.",
  },
];

export const STYLES = [
  { name: "Bharni", note: "filled colour" },
  { name: "Katchni", note: "fine line work" },
  { name: "Tantrik", note: "the sacred" },
  { name: "Godna", note: "tattoo forms" },
  { name: "Kohbar", note: "the marriage chamber" },
];

export const LITERATURE = [
  {
    title: "Born on Walls",
    text: "In the villages around Madhubani in Bihar, painting began as bhitti chitra — images made on the mud walls and floors of homes at weddings, births and festivals. Tradition remembers King Janaka commissioning paintings to celebrate the wedding of his daughter Sita. From those walls the art moved to handmade paper and canvas, and Madhubani painting today holds a Geographical Indication tag of its own.",
  },
  {
    title: "Twig, Nib & Natural Colour",
    text: "The line comes first, and it was never drawn with a brush alone — artists worked with twigs wrapped in cotton, with fingers, matchsticks and, in time, the fine steel nib that makes the dense katchni hatching possible. Colour once came from the household and the field: soot for black, turmeric for yellow, ochre, indigo, crushed flowers and leaves. Acrylic and poster colours now sit beside those older sources.",
  },
  {
    title: "The Rule of Fullness",
    text: "A Mithila painting rarely leaves an empty corner. This instinct — bharna, the filling — covers the ground between figures with hatching, dots, flowers and geometry until the surface hums. Double-line borders hold the field together, figures face forward with large lotus-shaped eyes, and symmetry gives even the liveliest scene a ceremonial stillness.",
  },
];

export const PILLARS = [
  {
    no: "I",
    title: "Tradition",
    text: "I ground my work in the classical Mithila vocabulary — its gods, its borders, its grammar of line — learned through workshops and my ongoing formal study at Shilp Kala Vidyapeeth.",
  },
  {
    no: "II",
    title: "Observation",
    text: "Nature, people, mythology and everyday life guide my eye — peacocks and fish remembered from my childhood notebooks, the exhibitions I visited in Patna, the Mithila textiles that fill my home.",
  },
  {
    no: "III",
    title: "Interpretation",
    text: "My own ideas now enter the traditional language — compositions that began as recreations have grown into my own interpretations.",
  },
  {
    no: "IV",
    title: "Craft",
    text: "Line before colour. Fine hatching, double-line borders, patient pattern work and a palette drawn from the earth — the discipline that lets the image hold.",
  },
];

export const CREDENTIALS = [
  {
    label: "Professional engagement",
    title: "25+ Years With Indian Folk Art",
    text: "My professional profile documents more than two decades of involvement with traditional Indian folk art and the artistic community.",
  },
  {
    label: "Government of India",
    title: "Licensed Artisan",
    text: "I hold an official license from the Government of India, Department of Handicrafts and Textiles, recognising my professional artisan status.",
  },
  {
    label: "Art education",
    title: "Art Educator",
    text: "I have teaching experience in art education, associated with Urmila Setu Sansthan.",
  },
  {
    label: "Current practice",
    title: "Mithila Art Practitioner",
    text: "I am currently pursuing formal study of traditional Mithila technique, mythology and festival culture at Shilp Kala Vidyapeeth.",
  },
];

export const ENQUIRY_TYPES = [
  "Commission an artwork",
  "Exhibition enquiry",
  "Workshop",
  "Collaboration",
  "General enquiry",
];

// Shown on /mithila-art and published as FAQPage structured data. Every answer
// restates what the site already says elsewhere, so the markup matches the page.
export const FAQS = [
  {
    q: "What is Mithila (Madhubani) painting?",
    a: "Mithila painting, also called Madhubani painting, is a folk art tradition from the Mithila region of Bihar, India. It began as bhitti chitra — images painted on the mud walls and floors of homes at weddings, births and festivals — and was carried largely by women for centuries. Today it is practised on handmade paper and canvas, and Madhubani painting holds a Geographical Indication (GI) tag.",
  },
  {
    q: "What are the main styles of Madhubani painting?",
    a: "Five classical styles are usually named: Bharni (filled colour), Katchni (fine line work), Tantrik (sacred imagery), Godna (forms drawn from tattoo patterns) and Kohbar (paintings of the marriage chamber).",
  },
  {
    q: "What do the fish, lotus and peacock mean in Mithila art?",
    a: "The fish stands for fertility, abundance and good fortune. The lotus signifies purity rising from still water and anchors prayers, goddesses and thresholds. The peacock expresses love, longing and monsoon joy. The sun and moon, painted as watchful faces, mark cosmic order, and the tree of life stands for lineage and continuity.",
  },
  {
    q: "What materials are used in Mithila painting?",
    a: "Artists traditionally drew with twigs wrapped in cotton, fingers and matchsticks, and later the fine steel nib used for dense katchni hatching. Colours came from the household and the field — soot for black, turmeric for yellow, ochre, indigo, crushed flowers and leaves. Acrylic and poster colours are now used alongside them.",
  },
  {
    q: "Why do Mithila paintings have no empty space?",
    a: "Mithila painting follows bharna, the filling: the ground between figures is covered with hatching, dots, flowers and geometry, held together by double-line borders, so that the whole surface carries energy edge to edge.",
  },
  {
    q: `Who is ${ARTIST.name}?`,
    a: `${ARTIST.name} is a Mithila / Madhubani artist based in Bhopal, Madhya Pradesh. A licensed artisan with the Government of India (Department of Handicrafts and Textiles) and an art educator with more than 25 years of involvement in Indian folk art, ${ARTIST.name} studies traditional Mithila technique, mythology and festival culture at Shilp Kala Vidyapeeth.`,
  },
  {
    q: `Can I commission a Mithila painting from ${ARTIST.name}?`,
    a: `Yes. Commissions, exhibition enquiries, workshops and collaborations can be requested through the contact form on this site, by email at ${ARTIST.email}, or by phone or WhatsApp on ${ARTIST.phone}.`,
  },
];
